/**
 * Billing Management Card
 *
 * Provides agents with access to billing-related actions via Stripe Customer Portal.
 * Centralized in /dashboard/wallet so agents don't need to look elsewhere.
 *
 * Actions available:
 * - Manage Payment Method (opens Stripe portal)
 * - View Invoices (opens Stripe portal)
 *
 * The card gracefully handles states:
 * - No Stripe customer (prepaid/trial without subscription)
 * - Portal loading state
 * - Error state
 */

import { useState } from "react";
import { CreditCard, Receipt, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface BillingManagementCardProps {
  hasStripeCustomer: boolean;
  contractType?: string;
}

export function BillingManagementCard({
  hasStripeCustomer,
  contractType,
}: BillingManagementCardProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const openPortal = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast({
          title: "Authentication required",
          description: "Please sign in again to access billing.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-portal-session", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error || !data?.url) {
        const message = error?.message || data?.error || "Could not open billing portal.";
        toast({
          title: "Billing portal unavailable",
          description: message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Open Stripe portal in a new tab
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("[BillingManagement] Portal error:", err);
      toast({
        title: "Error",
        description: "Could not open billing portal. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // For prepaid packages without Stripe customer, show a simpler card
  if (!hasStripeCustomer) {
    return (
      <div className="bg-card rounded-2xl border border-border p-5">
        <div className="flex items-center gap-2 mb-3">
          <CreditCard className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-display text-base font-semibold text-foreground">Billing</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          {contractType === "prepaid_package"
            ? "Your prepaid package does not have recurring billing. Contact your account manager for additional purchases."
            : "No billing account linked. Contact your account manager for billing questions."}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl border border-border p-5">
      <div className="flex items-center gap-2 mb-3">
        <CreditCard className="w-4 h-4 text-muted-foreground" />
        <h3 className="font-display text-base font-semibold text-foreground">Billing</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Manage your payment method, view invoices, and update billing details.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 justify-center gap-2"
          onClick={openPortal}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <CreditCard className="w-3.5 h-3.5" />
          )}
          {loading ? "Opening..." : "Payment Method"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1 justify-center gap-2"
          onClick={openPortal}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Receipt className="w-3.5 h-3.5" />
          )}
          {loading ? "Opening..." : "Invoices"}
          <ExternalLink className="w-3 h-3 opacity-40" />
        </Button>
      </div>
    </div>
  );
}
